/**
 * Activity phases (§2.7) — a UI-agnostic reducer over raw agent `StreamEvent`s.
 *
 * This is the shared replacement for the panel's per-step `ActivityStep` model.
 * It is consumed today by tender chat (via a store-level subscription) and is
 * designed so the editor panel can adopt it unchanged in stage 4.
 *
 * The §2.7 spec exists because the panel's step UI has four *measured* defects.
 * This reducer makes each impossible **by construction**, not by patching:
 *
 *  1. Title clobber on completion — a completing event downgrading a specific
 *     running title to a generic one. Here, completion events NEVER overwrite an
 *     existing title (they only fill an empty one); started/specific events own
 *     the title. See `applyTitle`.
 *  2. Immortal steps from status narration — the panel maps unknown `status`
 *     strings to brand-new step rows that carry no completing event and so never
 *     finish. Here, `status` NEVER creates a row; it only updates an existing
 *     row's detail (or is dropped). See the `status` branch.
 *  3. Fabricated bulk-close durations — the panel stamps `durationMs` on every
 *     row, including rows it bulk-closes at turn end. Here, **rows carry no
 *     duration field at all** (`ActivityPhase` has none). Only the turn-level
 *     total is tracked, and only from real signals (`metrics.latencyMs`).
 *  4. Per-search rows without rollup — the panel spawns a row per search. Here,
 *     every search signal (`evidence_search_*`, search-tool `tool_call_*`, and
 *     broadening `status` narration) aggregates into the ONE Searching row with a
 *     counter and accumulated detail.
 */

import type { StreamEvent } from '@/lib/agent-client'

// ===================
// Types
// ===================

/**
 * The fixed phase vocabulary (≤7 rows), in canonical render order.
 * `reviewing` is a document job's review step (R1 P3′); chat turns never
 * produce it, so a turn still shows at most six rows.
 */
export type PhaseKind =
  | 'understanding'
  | 'reading'
  | 'searching'
  | 'planning'
  | 'writing'
  | 'reviewing'
  | 'checking'

export type PhaseStatus = 'running' | 'completed' | 'failed'

/**
 * A single activity row. Deliberately carries NO duration/timestamp fields — the
 * "fabricated bulk-close durations" defect is impossible because there is nowhere
 * to fabricate one. Turn-level timing lives on `PhaseState`.
 */
export interface ActivityPhase {
  kind: PhaseKind
  status: PhaseStatus
  /** The MOST specific title seen for this phase; completion never downgrades it. */
  title: string
  detail?: string
}

/** The full reduced state for one turn. Passed whole to `<ActivityPhases>`. */
export interface PhaseState {
  /** The turn these phases belong to (drives the "new turn resets" rule). */
  turnId: string | null
  /** Ordered rows, at most one per `PhaseKind`, in canonical order. */
  phases: ActivityPhase[]
  /** Real turn duration (`turn_completed.metrics.latencyMs`, else wall-clock). */
  turnDurationMs: number | null
  /** Number of COMPLETED searches aggregated into the Searching row. */
  searchCount: number
  /** Sum of `candidateCount` across searches ("N excerpts"). */
  excerptCount: number
  /** Distinct source document names across searches ("from N documents"). */
  documentNames: string[]
  /** Word count, set only when a writing phase actually ran (drives the summary). */
  wordCount: number | null
  /** Wall-clock start, best-effort (used only if `latencyMs` is absent). */
  startedAtMs: number | null
  /**
   * True once the answer has begun streaming (first `assistant_message_chunk`).
   * Drives the "Answering" active label so the trigger stops narrating
   * "Understanding request" during the longest visible span. Not a phase row —
   * the vocabulary stays fixed at ≤6 — so the collapsed summary is unaffected.
   */
  answering: boolean
  /**
   * True once a write tool (write_to_editor / revise_draft) has RESULTED
   * successfully in this turn. The Writing phase is a single upsert row, so a
   * later rejected write call would otherwise repaint a write that already
   * landed as "Could not complete" — measured on a turn the backend recorded
   * as `apply_succeeded`, where the single-write-per-turn guard rejected the
   * model's redundant second call.
   */
  writeSucceeded: boolean
  /**
   * True once any search in this turn has returned (an evidence completion or a
   * successful search-tool result). The Searching row rolls every search into
   * one row, so without this a single refused or errored call — even the last
   * of twenty — repainted it, and the turn header, as failed on a turn whose
   * other searches succeeded. The row is failed only when every search failed.
   */
  searchSucceeded: boolean
  /**
   * True once a `set_plan` call has RESULTED successfully in this turn. A plan
   * that landed is the plan the user has: a failed attempt before it (the model
   * retried) or a rejected redundant call after it must not repaint the one
   * Planning row as "Could not create plan".
   */
  planSucceeded: boolean
}

// ===================
// Constants
// ===================

const PHASE_ORDER: Record<PhaseKind, number> = {
  understanding: 0,
  reading: 1,
  searching: 2,
  planning: 3,
  writing: 4,
  reviewing: 5,
  checking: 6,
}

const DEFAULT_TITLE: Record<PhaseKind, string> = {
  understanding: 'Understanding request',
  reading: 'Reading your workspace',
  searching: 'Searching documents',
  planning: 'Planning',
  writing: 'Writing draft',
  reviewing: 'Reviewing draft',
  checking: 'Checking requirements',
}

const SEARCH_TOOLS = new Set(['search_evidence', 'search_library', 'search_strategy'])

/**
 * Phases where one success outweighs any number of failed calls — the same rule
 * the live reducer applies (`searchSucceeded`, `writeSucceeded`,
 * `planSucceeded`), so a turn reads the same before and after a reload.
 * Searching rolls many searches into one row; a write or plan that landed stays
 * landed whether the failed call came before it (a retry) or after it (a
 * rejected redundant call). Every other phase keeps "any failure fails the
 * phase".
 */
const SUCCESS_WINS_KINDS: ReadonlySet<PhaseKind> = new Set<PhaseKind>([
  'reading',
  'searching',
  'planning',
  'writing',
  'reviewing',
])

/**
 * A search that came back empty: the backend persists "No relevant documents
 * found"; live says "No matching evidence found". Either describes ONE search,
 * so it must never describe a Searching row whose other searches found evidence.
 */
const EMPTY_SEARCH_NOTE = /^No (relevant documents|matching evidence) found/i

/**
 * Workspace read tools → what the row should say was read. These answer from
 * the user's own workspace, not from tender documents, so they get their own
 * row: labelling them "Searching documents" promised sources the turn never
 * had. Detail is the thing read, so repeated reads roll into one row without
 * fighting over the title.
 */
const WORKSPACE_READ_LABEL: Record<string, string> = {
  list_response_content: 'Response outline',
  read_response_content: 'Response draft',
  read_brief: 'Bid brief',
  read_analysis: 'Tender analysis',
  list_library: 'Library folders',
  // The workspace-map reads (AI redesign Task B3).
  read_comments: 'Comments',
  read_risk_register: 'Risk register',
  read_go_no_go: 'Go/No-Go',
  read_pack_asks: 'Waiting on the team',
  read_tender_overview: 'Tender summary',
  read_compliance_positions: 'Compliance positions',
  read_company_profile: 'Company profile',
  read_plan: 'Response plan',
  read_draft_section: 'Current draft',
}

/**
 * `read_document` is deliberately NOT a workspace read. It emits a real
 * `evidence_search_completed` (mode 'tool'), which owns the Searching row and
 * the excerpt/document counts, so giving it a Reading row too would narrate one
 * tool call as two steps. `classifyPersistedTitle` keeps its persisted title
 * ("Reading document") on the Searching row for the same reason — the shim must
 * classify a title the way live classifies the tool behind it.
 */

/** Non-search tools → the phase they belong to. `respond_to_user` intentionally absent (no row). */
const TOOL_PHASE: Record<string, PhaseKind> = {
  write_to_editor: 'writing',
  revise_draft: 'writing',
  set_plan: 'planning',
  verify_draft: 'checking',
}

// ===================
// Public API
// ===================

/**
 * The persisted step shape stored in `assistant_metadata.activitySteps` by the
 * (now-retired) `format-activity-step` path. Kept as the shim's input type so
 * already-persisted historical turns can still render their activity — the shim
 * folds them into the new phase vocabulary. NOTE: `durationMs` is intentionally
 * ignored (the "fabricated bulk-close durations" defect lived on that field).
 */
export interface PersistedActivityStep {
  title: string
  detail?: string
  status: 'completed' | 'failed'
  durationMs?: number
}

/**
 * History shim — map the OLD persisted `ActivityStep[]` into a terminal
 * `PhaseState` so already-persisted turns keep an activity display after the
 * step system is retired. Faithful, never inventive:
 *  - Every search-flavoured step aggregates into the ONE Searching row
 *    (`searchCount` reflects how many collapsed), matching the live rollup.
 *  - Non-search steps map to their phase by title; the real persisted title is
 *    preserved (it may carry a section name — "Writing Executive Summary").
 *  - A `failed` persisted step marks its phase failed — except Searching,
 *    Planning and Writing, which are failed only when every step of that kind
 *    failed (matching the live reducer; see `SUCCESS_WINS_KINDS`).
 *  - No durations are reconstructed (there is nowhere on `ActivityPhase` to put
 *    one). Turn-level timing stays null — the collapsed summary shows only real
 *    signals (documents/words), never a fabricated total.
 * Steps whose title maps to no phase are dropped rather than invented into a row.
 */
export function hydratePhasesFromSteps(steps: PersistedActivityStep[] | undefined): PhaseState {
  const base = initialPhaseState()
  if (!Array.isArray(steps) || steps.length === 0) return base

  const byKind = new Map<PhaseKind, ActivityPhase>()
  // Last detail from a step that succeeded, per kind (see SUCCESS_WINS_KINDS).
  const successDetail = new Map<PhaseKind, string>()
  let searchCount = 0

  for (const step of steps) {
    // Skip malformed persisted entries (null array slots, title-less/non-string
    // titles) — a bad entry must not crash the panel's history render path.
    if (typeof step?.title !== 'string') continue
    const kind = classifyPersistedTitle(step.title)
    if (!kind) continue
    const failed = step.status === 'failed'
    if (kind === 'searching') searchCount++
    if (!failed && step.detail) {
      // Persisted details are per search, and the row shows one of them. An
      // empty search never replaces one that found evidence — otherwise a turn
      // whose LAST search came back empty reloads as "No relevant documents
      // found" over a draft full of citations (dev turn 84aee8dd).
      const current = successDetail.get(kind)
      const foundNothing = kind === 'searching' && EMPTY_SEARCH_NOTE.test(step.detail)
      if (!foundNothing || current === undefined || EMPTY_SEARCH_NOTE.test(current)) {
        successDetail.set(kind, step.detail)
      }
    }

    const existing = byKind.get(kind)
    if (!existing) {
      byKind.set(kind, {
        kind,
        status: failed ? 'failed' : 'completed',
        // Searches collapse into one row → use the canonical Searching title;
        // other kinds keep their real (possibly section-specific) title.
        title: kind === 'searching' ? DEFAULT_TITLE.searching : step.title,
        ...(step.detail ? { detail: step.detail } : {}),
      })
    } else {
      // Aggregate a repeat of the same kind; keep the last real detail. Title
      // is not downgraded.
      byKind.set(kind, {
        ...existing,
        status: aggregateStatus(kind, existing.status, failed),
        ...(step.detail ? { detail: step.detail } : {}),
      })
    }
  }

  const phases = [...byKind.values()]
    .map((phase) => {
      // A phase that ended completed shows what succeeded — never the note of
      // a failed call it outweighed ("Could not complete" beside a tick).
      if (!SUCCESS_WINS_KINDS.has(phase.kind) || phase.status !== 'completed') return phase
      const detail = successDetail.get(phase.kind)
      return {
        kind: phase.kind,
        status: phase.status,
        title: phase.title,
        ...(detail ? { detail } : {}),
      }
    })
    .sort((a, b) => PHASE_ORDER[a.kind] - PHASE_ORDER[b.kind])
  return { ...base, phases, searchCount }
}

/** One saved fact of a document job, already worded (src/lib/document-job-progress.ts). */
export interface JobPhaseStep {
  kind: PhaseKind
  status: 'completed' | 'failed'
  title: string
}

/**
 * Phases for a document job, from its saved progress facts (R1 P3′). Each
 * phase row carries the latest fact of its kind as its title ("Wrote 3 of 8:
 * Methodology"); for a success-wins kind the latest SUCCESSFUL fact, and the
 * row is failed only when every fact of that kind failed. While the job runs,
 * its latest fact is the live row — the job never shows a title it has not
 * saved — and before its first fact a single live "Started" row.
 */
export function hydratePhasesFromJobSteps(steps: readonly JobPhaseStep[], options: { running: boolean }): PhaseState {
  const base = initialPhaseState()
  const byKind = new Map<PhaseKind, ActivityPhase>()
  for (const step of steps) {
    const failed = step.status === 'failed'
    const existing = byKind.get(step.kind)
    if (!existing) {
      byKind.set(step.kind, makePhase(step.kind, step.status, step.title))
      continue
    }
    const status = aggregateStatus(step.kind, existing.status, failed)
    const keepTitle = SUCCESS_WINS_KINDS.has(step.kind) && failed && existing.status === 'completed'
    byKind.set(step.kind, { kind: step.kind, status, title: keepTitle ? existing.title : step.title })
  }
  const phases = [...byKind.values()].sort((a, b) => PHASE_ORDER[a.kind] - PHASE_ORDER[b.kind])
  if (!options.running) return { ...base, phases }
  if (phases.length === 0) return { ...base, phases: [makePhase('understanding', 'running', 'Started')] }
  const last = phases[phases.length - 1]!
  return { ...base, phases: [...phases.slice(0, -1), { ...last, status: 'running' }] }
}

function aggregateStatus(kind: PhaseKind, previous: PhaseStatus, stepFailed: boolean): PhaseStatus {
  if (SUCCESS_WINS_KINDS.has(kind)) {
    return previous === 'completed' || !stepFailed ? 'completed' : 'failed'
  }
  return previous === 'failed' || stepFailed ? 'failed' : 'completed'
}

/**
 * Classify an OLD persisted step title into the new phase vocabulary. Order
 * matters: `checking`/`planning` are tested before `writing` because their old
 * titles ("Checking draft against requirements", "Reviewing response plan")
 * contain the substrings "draft"/"plan".
 */
function classifyPersistedTitle(title: string): PhaseKind | null {
  const t = title.toLowerCase()
  if (
    t.includes('search') ||
    t.includes('relevant excerpt') ||
    t.includes('relevant document') ||
    t.includes('relevant section') ||
    t.includes('finding the most relevant') ||
    t.includes('broadening') ||
    t.includes('reviewing evidence') ||
    // read_document's persisted title. Live it lands on Searching (it emits a
    // real evidence event) — the shim must agree. Must precede the reading rule.
    t.includes('reading document')
  ) {
    return 'searching'
  }
  // Workspace reads only — the titles the tools in WORKSPACE_READ_LABEL persist,
  // including the two legacy ones ("Reading current draft", "Reviewing response
  // plan"). Matched explicitly rather than on a bare "reading", which also
  // swallowed "Reading document".
  if (
    t.includes('reading your workspace') ||
    t.includes('reading current draft') ||
    t.includes('reviewing response plan')
  ) {
    return 'reading'
  }
  if (t.includes('verify') || t.includes('verification') || t.includes('checking')) {
    return 'checking'
  }
  if (t.includes('structuring') || t.includes('planning') || t.includes('plan')) {
    return 'planning'
  }
  if (
    t.includes('writing') ||
    t.includes('revising') ||
    t.includes('revision') ||
    t.includes('drafting') ||
    t.includes('draft')
  ) {
    return 'writing'
  }
  // Both spellings: activity titles persist in agent_turns, and rows written
  // before the copy moved to Australian English still say "Analyzing".
  if (
    t.includes('analysing') ||
    t.includes('analyzing') ||
    t.includes('processing') ||
    t.includes('understanding')
  ) {
    return 'understanding'
  }
  return null
}

export function initialPhaseState(): PhaseState {
  return {
    turnId: null,
    phases: [],
    turnDurationMs: null,
    searchCount: 0,
    excerptCount: 0,
    documentNames: [],
    wordCount: null,
    startedAtMs: null,
    answering: false,
    writeSucceeded: false,
    searchSucceeded: false,
    planSucceeded: false,
  }
}

/**
 * Optimistic pre-turn state, seeded by the stores at send time so the activity
 * display is never blank while the request is in flight (cold start + the
 * pipeline's pre-`turn_started` setup can take seconds). `turnId` stays null,
 * so the first real event triggers the new-turn reset above and replaces this
 * state wholesale — optimistic rows can never leak into the real turn.
 */
export function optimisticPhaseState(): PhaseState {
  return {
    ...initialPhaseState(),
    phases: [makePhase('understanding', 'running')],
  }
}

/**
 * Pure reducer. Returns the SAME reference when an event is a no-op (heartbeat,
 * unknown types, dropped status) so subscribers can cheaply skip re-renders.
 */
export function reduceActivityPhases(state: PhaseState, event: StreamEvent): PhaseState {
  if (event.eventType === 'heartbeat') return state

  // New turn (explicit `turn_started`, or the first event we see mid-stream when
  // the subscription attached after `turn_started` already fired) → fresh state
  // seeded with the implicit Understanding phase. This is the mid-turn-mount seam.
  let working = state
  if (event.turnId && event.turnId !== state.turnId) {
    working = {
      ...initialPhaseState(),
      turnId: event.turnId,
      startedAtMs: parseEventTime(event),
      phases: [makePhase('understanding', 'running')],
    }
    // A bare `turn_started` needs nothing further.
    if (event.eventType === 'turn_started') return working
  }

  switch (event.eventType) {
    case 'turn_started':
      // Same-turn duplicate (rare) — already seeded above or on a prior call.
      return working

    case 'status':
      return applyStatus(working, str(event.data['message']))

    case 'evidence_search_started':
      // A search is starting — ensure the Searching row exists and is running.
      // Started never increments the counter (completion events do), so a
      // started/completed pair counts once.
      return upsert(working, 'searching', { status: 'running' })

    case 'evidence_search_completed':
      return applySearchCompleted(working, event)

    case 'assistant_message_chunk':
      // The answer is streaming — complete the still-running Understanding row
      // and flip into "Answering" so the trigger narrates the span honestly.
      return applyAssistantChunk(working)

    case 'tool_call_started':
      return applyToolStarted(working, event)

    case 'tool_call_result':
      return applyToolResult(working, event)

    case 'turn_completed':
      // A turn that handed off to the background worker is NOT finished: the
      // pipeline emits `turn_completed` with `status: 'async_pending'` the
      // instant it dispatches the job, and the worker then writes for another
      // one to three minutes. Sweeping the rows here collapsed the block to
      // "done in 12.3s" while the section was still being drafted.
      if (str(event.data['status']) === 'async_pending') return working
      return finalizeTurn(working, event, 'completed')

    case 'turn_error':
      return finalizeTurn(working, event, 'failed')

    // Everything else (chunks, patches, coverage, suggestions, …) has no row.
    default:
      return working === state ? state : working
  }
}

/**
 * A background job's own progress narration ("Generating "Approach" (1/2)...")
 * routed exactly like a streamed `status` event: onto the running row's detail,
 * or dropped. It can never create a row — the job has no completing event of
 * its own, so a row made here would be the immortal-step defect again.
 */
export function applyProgressNarration(state: PhaseState, message: string): PhaseState {
  return applyStatus(state, message)
}

/**
 * Close a turn whose end arrives without a stream event — a background job that
 * finished under the poller. Same sweep `turn_completed` / `turn_error` apply:
 * every still-running row takes the terminal status. The duration is the real
 * wall-clock span from the turn's first event, or stays unknown.
 */
export function settleActivityPhases(
  state: PhaseState,
  outcome: 'completed' | 'failed',
  endedAtMs: number = Date.now()
): PhaseState {
  if (!state.phases.some((p) => p.status === 'running')) return state
  const phases = state.phases.map((p) => (p.status === 'running' ? { ...p, status: outcome } : p))
  const turnDurationMs =
    state.turnDurationMs ?? (state.startedAtMs != null ? Math.max(0, endedAtMs - state.startedAtMs) : null)
  return { ...state, phases, turnDurationMs }
}

/**
 * Collapsed summary line — shows something REAL (no fabricated per-step timing).
 * "Found evidence in N documents · Xs total", plus "~N words" when a writing
 * phase ran. Returns null when there is nothing meaningful to summarise.
 *
 * The count is documents the turn found evidence IN (distinct names across the
 * returned citations), never documents searched — a library search sweeps the
 * whole library and would have read as "Searched 1 document".
 */
export function summarizePhases(state: PhaseState): string | null {
  if (state.phases.length === 0) return null

  const parts: string[] = []
  const docs = state.documentNames.length
  if (docs > 0) parts.push(`Found evidence in ${docs} document${docs === 1 ? '' : 's'}`)
  if (state.wordCount != null && state.wordCount > 0) parts.push(`~${state.wordCount} words`)
  if (state.turnDurationMs != null) parts.push(`${(state.turnDurationMs / 1000).toFixed(1)}s total`)

  // Nothing measurable to report. "Done" beside the failed-turn alert read as
  // success, and a reloaded turn always lands here (history carries no counts).
  if (parts.length === 0) return hasFailedPhase(state) ? 'Finished with problems' : 'Done'
  return parts.join(' · ')
}

/** The row that transient narration/detail should attach to (last running, else last). */
export function activePhase(state: PhaseState): ActivityPhase | undefined {
  for (let i = state.phases.length - 1; i >= 0; i--) {
    if (state.phases[i]!.status === 'running') return state.phases[i]
  }
  return state.phases[state.phases.length - 1]
}

export function hasFailedPhase(state: PhaseState): boolean {
  return state.phases.some((p) => p.status === 'failed')
}

/**
 * The label the streaming trigger should show. A running phase always wins; once
 * the answer is streaming (Understanding completed, nothing else running) it
 * reads "Answering" instead of falling back to the completed Understanding title.
 */
export function activeLabel(state: PhaseState): string {
  const active = activePhase(state)
  if (active?.status === 'running') return active.title
  if (state.answering) return 'Answering'
  return active?.title ?? 'Working'
}

// ===================
// Event handlers
// ===================

function applyStatus(state: PhaseState, message: string | undefined): PhaseState {
  const routed = classifyStatus(message)
  if (routed.target === 'drop') return state

  if (routed.target === 'search') {
    // Broadening narration aggregates into the Searching row — but never CREATES
    // one (that would resurrect the immortal-step defect). Drop if absent.
    const idx = state.phases.findIndex((p) => p.kind === 'searching')
    if (idx === -1) return state
    return patchPhaseAt(state, idx, { detail: routed.detail })
  }

  // Generic narration → the active row's detail. No row exists yet → drop.
  const active = activePhase(state)
  if (!active) return state
  const idx = state.phases.findIndex((p) => p.kind === active.kind)
  return patchPhaseAt(state, idx, { detail: routed.detail })
}

function applySearchCompleted(state: PhaseState, event: StreamEvent): PhaseState {
  const d = event.data
  // FIX-2: the end-of-turn resolved-pages re-emit refreshes citation payloads
  // for the stores but is NOT a search — never count it (live must equal the
  // persisted activity line, which is snapshotted before the re-emit).
  if (str(d['mode']) === 'resolution') return state
  const candidateCount = num(d['candidateCount']) ?? 0
  const retrievalStatus = str(d['retrievalStatus'])
  const docNames = collectDocNames(d)

  const mergedNames = mergeUnique(state.documentNames, docNames)
  const next: PhaseState = {
    ...state,
    searchCount: state.searchCount + 1,
    excerptCount: state.excerptCount + candidateCount,
    documentNames: mergedNames,
    searchSucceeded: true,
  }
  // "No matching evidence found" only while the turn has found nothing at all:
  // the row rolls up every search, so one empty search after others found
  // excerpts must not overwrite the running total.
  const detail =
    retrievalStatus === 'empty' && next.excerptCount === 0
      ? 'No matching evidence found'
      : searchDetail(next.searchCount, next.excerptCount, mergedNames.length)
  // The Searching row is marked completed on each completing search; a later
  // `evidence_search_started`/search-tool start re-activates it. Turn end sweeps
  // any still-running row. Nothing is immortal.
  return upsert(next, 'searching', { status: 'completed', detail })
}

function applyToolStarted(state: PhaseState, event: StreamEvent): PhaseState {
  const toolName = str(event.data['toolName'])
  if (!toolName || toolName === 'respond_to_user') return state

  if (SEARCH_TOOLS.has(toolName)) {
    return upsert(state, 'searching', { status: 'running' })
  }

  const readLabel = WORKSPACE_READ_LABEL[toolName]
  if (readLabel) {
    return upsert(state, 'reading', { status: 'running', detail: readLabel })
  }

  const kind = TOOL_PHASE[toolName]
  if (!kind) return state // unknown tool → no row (keeps the vocabulary fixed)

  if (kind === 'writing') {
    const target = str(event.data['sectionTitle'])
    // A section-bearing title is the most specific title — set it authoritatively.
    return upsert(state, 'writing', {
      status: 'running',
      ...(target ? { title: `Writing ${target}` } : {}),
    })
  }
  return upsert(state, kind, { status: 'running' })
}

function applyToolResult(state: PhaseState, event: StreamEvent): PhaseState {
  const d = event.data
  const toolName = str(d['toolName'])
  if (!toolName || toolName === 'respond_to_user') return state
  const failed = d['status'] === 'failed'
  const finalStatus: PhaseStatus = failed ? 'failed' : 'completed'

  if (SEARCH_TOOLS.has(toolName)) {
    // A tool search emits the full quartet `tool_call_started` →
    // `evidence_search_started` → `evidence_search_completed` → `tool_call_result`.
    // `evidence_search_completed` OWNS counting and the detail; this result only
    // drives the status transition (and failure marking). Counting here would
    // double-count the tool search, and re-deriving the detail would clobber an
    // "empty" ("No matching evidence found") detail. Completion NEVER passes a
    // title → the running title survives untouched.
    //
    // A failed call after a search already succeeded closes the row as
    // completed, not failed — see `searchSucceeded`. Only a turn whose every
    // search failed shows the row failed.
    if (failed) {
      return upsert(state, 'searching', { status: state.searchSucceeded ? 'completed' : 'failed' })
    }
    const next = state.searchSucceeded ? state : { ...state, searchSucceeded: true }
    return upsert(next, 'searching', { status: 'completed' })
  }

  if (WORKSPACE_READ_LABEL[toolName]) {
    // No title/detail passed — the specific thing read, set at start, survives.
    return upsert(state, 'reading', { status: finalStatus })
  }

  const kind = TOOL_PHASE[toolName]
  if (!kind) return state

  if (kind === 'writing') {
    const wordCount = num(d['wordCount'])
    const existing = state.phases.find((p) => p.kind === 'writing')

    // A write that already landed is never repainted as a failure. The turn can
    // call write_to_editor again and the backend's single-write-per-turn guard
    // rejects the redundant call; that rejection is about the SECOND call, not
    // the draft the user already has. Its own `tool_call_started` puts the one
    // shared row back to `running`, which is why the completed status alone
    // cannot be trusted here — `writeSucceeded` remembers the success.
    if (failed && (state.writeSucceeded || existing?.status === 'completed')) {
      const restored =
        state.wordCount != null && state.wordCount > 0
          ? `~${state.wordCount} words`
          : existing?.detail
      return upsert(state, 'writing', {
        status: 'completed',
        ...(restored ? { detail: restored } : {}),
      })
    }

    const next: PhaseState = {
      ...state,
      ...(wordCount != null && wordCount > 0 ? { wordCount } : {}),
      ...(failed ? {} : { writeSucceeded: true }),
    }
    const detail = failed
      ? 'Could not complete'
      : wordCount != null && wordCount > 0
        ? `~${wordCount} words`
        : undefined
    // No title passed — a specific "Writing {section}" set at start is preserved.
    return upsert(next, 'writing', { status: finalStatus, ...(detail ? { detail } : {}) })
  }

  if (kind === 'planning') {
    // Same rule as writing: once a plan landed, a rejected later call closes
    // the row completed and keeps the landed plan's detail. Its own
    // `tool_call_started` already put the row back to `running`.
    if (failed && state.planSucceeded) {
      return upsert(state, 'planning', { status: 'completed' })
    }
    if (failed) {
      return upsert(state, 'planning', { status: 'failed', detail: 'Could not create plan' })
    }

    const sectionCount = num(d['sectionCount'])
    const next: PhaseState = { ...state, planSucceeded: true }
    if (sectionCount != null && sectionCount > 0) {
      return upsert(next, 'planning', { status: 'completed', detail: `${sectionCount} sections` })
    }
    // A success with no count to show must not keep an earlier attempt's
    // "Could not create plan" beside a tick. `upsert` only ever sets a detail,
    // so drop it here.
    const completed = upsert(next, 'planning', { status: 'completed' })
    return dropDetail(completed, 'planning')
  }

  // checking (verify_draft)
  return upsert(state, kind, { status: finalStatus })
}

function applyAssistantChunk(state: PhaseState): PhaseState {
  const uIdx = state.phases.findIndex((p) => p.kind === 'understanding')
  const understandingRunning = uIdx !== -1 && state.phases[uIdx]!.status === 'running'
  // Later chunks are a no-op (same reference) once we've already flipped in.
  if (!understandingRunning && state.answering) return state
  let next = understandingRunning ? patchPhaseAt(state, uIdx, { status: 'completed' }) : state
  if (!next.answering) next = { ...next, answering: true }
  return next
}

function finalizeTurn(state: PhaseState, event: StreamEvent, sweep: PhaseStatus): PhaseState {
  const latency = num((event.data['metrics'] as Record<string, unknown> | undefined)?.['latencyMs'])
  const turnDurationMs =
    latency != null
      ? latency
      : state.startedAtMs != null
        ? Math.max(0, parseEventTime(event) - state.startedAtMs)
        : null

  // Sweep every still-running row to the terminal status — nothing stays running.
  const phases = state.phases.map((p) =>
    p.status === 'running' ? { ...p, status: sweep } : p
  )
  return { ...state, phases, turnDurationMs }
}

// ===================
// Phase-array helpers
// ===================

function makePhase(kind: PhaseKind, status: PhaseStatus, title?: string, detail?: string): ActivityPhase {
  return {
    kind,
    status,
    title: title ?? DEFAULT_TITLE[kind],
    ...(detail ? { detail } : {}),
  }
}

/**
 * Insert or update the phase of `kind`, keeping the array in canonical order.
 *
 * Title rule (defeats the clobber defect): a `title` passed here is treated as a
 * MORE-SPECIFIC upgrade and overwrites. Completion handlers deliberately pass NO
 * title, so a running specific title is never downgraded.
 */
function upsert(
  state: PhaseState,
  kind: PhaseKind,
  patch: { status?: PhaseStatus; title?: string; detail?: string }
): PhaseState {
  const idx = state.phases.findIndex((p) => p.kind === kind)
  if (idx === -1) {
    const created = makePhase(kind, patch.status ?? 'running', patch.title, patch.detail)
    const phases = [...state.phases, created].sort((a, b) => PHASE_ORDER[a.kind] - PHASE_ORDER[b.kind])
    return { ...state, phases }
  }
  return patchPhaseAt(state, idx, patch)
}

function dropDetail(state: PhaseState, kind: PhaseKind): PhaseState {
  const idx = state.phases.findIndex((p) => p.kind === kind)
  const existing = state.phases[idx]
  if (!existing || existing.detail === undefined) return state
  const phases = [...state.phases]
  phases[idx] = { kind: existing.kind, status: existing.status, title: existing.title }
  return { ...state, phases }
}

function patchPhaseAt(
  state: PhaseState,
  idx: number,
  patch: { status?: PhaseStatus; title?: string; detail?: string }
): PhaseState {
  const existing = state.phases[idx]
  if (!existing) return state
  const updated: ActivityPhase = {
    ...existing,
    ...(patch.status ? { status: patch.status } : {}),
    ...(patch.title ? { title: patch.title } : {}), // only ever an upgrade (see upsert)
    ...(patch.detail ? { detail: patch.detail } : {}),
  }
  const phases = [...state.phases]
  phases[idx] = updated
  return { ...state, phases }
}

// ===================
// Small utilities
// ===================

function searchDetail(searchCount: number, excerptCount: number, docCount: number): string {
  const searches = `${searchCount} search${searchCount === 1 ? '' : 'es'}`
  if (excerptCount > 0 && docCount > 0) {
    return `${searches} · ${excerptCount} excerpt${excerptCount === 1 ? '' : 's'} from ${docCount} document${docCount === 1 ? '' : 's'}`
  }
  if (excerptCount > 0) {
    return `${searches} · ${excerptCount} excerpt${excerptCount === 1 ? '' : 's'}`
  }
  return searches
}

/**
 * Map a transient `status` message to a routing decision. It can ONLY drop, or
 * update the Searching row's detail, or update the active row's detail. It can
 * never create a row — that is what kills the immortal-step defect.
 */
function classifyStatus(
  message: string | undefined
): { target: 'drop' } | { target: 'search' | 'active'; detail: string } {
  if (!message) return { target: 'drop' }
  const lower = message.toLowerCase()

  if (lower.includes('thinking') || lower.includes('preparing draft')) return { target: 'drop' }

  if (lower.includes('no matching evidence')) return { target: 'search', detail: 'No matching evidence found' }
  if (lower.includes('broadening') || lower.includes('expanding'))
    return { target: 'search', detail: 'Broadening the search' }
  if (lower.includes('keyword search') || lower.includes('basic search'))
    return { target: 'search', detail: 'Broadening the search approach' }
  if (lower.includes('finding the most relevant'))
    return { target: 'search', detail: 'Finding the most relevant sections' }

  // Generic narration → active row detail (never a new row).
  return { target: 'active', detail: message.replace(/\.+$/, '') }
}

function collectDocNames(data: Record<string, unknown>): string[] {
  const names: string[] = []
  const docNames = data['documentNames']
  if (Array.isArray(docNames)) {
    for (const n of docNames) if (typeof n === 'string' && n.length > 0) names.push(n)
  }
  // `citations` is the tender path's field; `libraryCitations` is the library
  // path's. Reading only the first made every library-only turn look like it
  // touched nothing.
  for (const key of ['citations', 'libraryCitations'] as const) {
    const citations = data[key]
    if (!Array.isArray(citations)) continue
    for (const c of citations) {
      const name = (c as Record<string, unknown>)?.['documentName']
      if (typeof name === 'string' && name.length > 0) names.push(name)
    }
  }
  return names
}

function mergeUnique(existing: string[], incoming: string[]): string[] {
  if (incoming.length === 0) return existing
  const set = new Set(existing)
  let changed = false
  for (const n of incoming) {
    if (!set.has(n)) {
      set.add(n)
      changed = true
    }
  }
  return changed ? [...set] : existing
}

function parseEventTime(event: StreamEvent): number {
  const t = Date.parse(event.timestamp)
  return Number.isNaN(t) ? Date.now() : t
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined
}

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}
